import { useState, useEffect, useMemo } from "react";
import { Col, Row, Table, Modal, Form } from "react-bootstrap";
import FormGroupTile from "../../../../components/core/basic/FormGroupTile";
import FormGroupLabel from "../../../../components/core/basic/FormGroupLabel";
import { Button } from "../../../../components/core/basic/Button";
import { PRODUCT_AMAZON_LINKS } from "../../../../utils/marketplaceConstants";
import api from "../../../../utils/api";
import { useFetch } from "../../../../hooks/api";
import { cut } from "../../../../utils/formatText";
import { formatCurrency, formatNumber } from "../../../../utils/formatNumber";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { PlanSlider } from "../PricingSlider";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SLIDER_HELP } from "../SliderHelp";
import { useForm } from "react-hook-form";
import InlineProgramForm from "../../../../components/custom/programs/InlineProgramForm";
import { TbPencilCog } from "react-icons/tb";
import { CurrencyInput, NumberInput } from "../../../../components/fields/base/NumberInput";
import { HoverInfo } from "../../../../components/core/basic/Tooltip";
import { useNamedSteps } from "../../../../hooks/useNamedSteps";
import { MonthlyEstimates } from "../PricingPlan/MonthlyEstimates";
import { Flex, HStack, VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { ExplainerBox } from "../../../../components/core/basic/ExplainerBox";
import { useAppSelector } from "../../../../redux/hooks";
import { usePreviewPlanCreation } from "../../api/usePreviewPlanCreation";
import { ProductImageURL } from "../../../../components/custom/category/listings/ProductImage";
import { FaCaretDown, FaCaretRight } from "react-icons/fa6";
import { percentChange } from "../../../../utils/math";
import { TextField } from "../../../../components/fields/base/TextField";
import { IoWarningOutline } from "react-icons/io5";
import { Checkbox } from "../../../../components/fields/base/Checkbox";
import { DevErrorDisplay } from "../../../../components/core/blocks/DevErrorDisplay";
import { useModuleEnabled } from "../../../../redux/selectors/modules";

export const EnrollProductsInPricingModalForm = ({
  showEnroll,
  setShowEnroll,
  selectedProducts,
  initialMinPrice,
  initialMaxPrice,
  isMultiAsin,
  marketPlace,
  category,
  productById,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["pricingv2"]);
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");

  const actionTypeOptions = useMemo(
    () => [
      { label: t(($) => $.pricingv2["Match their price"]), value: "match" },
      { label: t(($) => $.pricingv2["Beat their price"]), value: "decrease" },
      { label: t(($) => $.pricingv2["Premium over their price"]), value: "increase" },
    ],
    [t],
  );

  const strategyOptions = useMemo(
    () => [
      { label: t(($) => $.pricingv2["Dynamic Pricing"]), value: "dynamic" },
      {
        label: t(($) => $.pricingv2["Margin Profiling"]),
        value: "margin_profiling",
        programTemplate: "MARGIN_PROFILING",
      },
      {
        label: t(($) => $.pricingv2["Best Seller Rank Optimization"]),
        value: "bsr",
      },
      {
        label: t(($) => $.pricingv2["Competitor Price Matching"]),
        value: "competitor",
      },
      {
        label: t(($) => $.pricingv2["Clearance Pricing"]),
        value: "clearance",
        programTemplate: "CLEARANCE",
      },
      {
        label: t(($) => $.pricingv2["Custom Rules"]),
        value: "rules_engine",
        programTemplate: "CUSTOM",
      },
    ],
    [t],
  );

  const steps = isMultiAsin ? ["group", "enroll", "preview", "done"] : ["enroll", "preview", "done"];
  const [currentStep, stepHelpers] = useNamedSteps(steps);

  const [targetBSR, setTargetBSR] = useState(0);
  const [currentBSR, setCurrentBSR] = useState(0);
  const [minPrice, setMinPrice] = useState(initialMinPrice);
  const [maxPrice, setMaxPrice] = useState(initialMaxPrice);
  const [planMode, setPlanMode] = useState(0);
  const [planStrategy, setPlanStrategy] = useState(strategyOptions[0]);

  const [validationErrors, setValidationErrors] = useState({});

  const [competitorList, setCompetitorList] = useState([]);
  const [chosenCompetitor, setChosenCompetitor] = useState(null);

  const [competitorAction, setCompetitorAction] = useState(actionTypeOptions[0]);
  const [competitorValue, setCompetitorValue] = useState(null);
  const [competitorUnit, setCompetitorUnit] = useState("percent");
  const [createManyPlans, setCreateManyPlans] = useState(isMultiAsin);
  const [overrideMargin, setOverrideMargin] = useState(false);
  const [overrideMarginValue, setOverrideMarginValue] = useState(null);
  const [overrideVolume, setOverrideVolume] = useState(false);
  const [overrideVolumeValue, setOverrideVolumeValue] = useState(null);
  const [overrideSupply, setOverrideSupply] = useState(false);
  const [overrideSupplyValue, setOverrideSupplyValue] = useState(null);
  const [intentionValidator, setIntentionValidator] = useState("");
  const [salesNet, setSalesNet] = useState(null);

  const queryClient = useQueryClient();

  const programForm = useForm({});

  const previewQuery = usePreviewPlanCreation(
    {
      product_list: selectedProducts,
      min_price: minPrice,
      max_price: maxPrice,
      is_multiple_plans: createManyPlans,
    },
    { enabled: currentStep === "preview" },
  );

  const confirmPlan = async () => {
    const planType = planStrategy;
    const payload = {
      name: "",
      plan_type: planType.value,
      target_bsr: targetBSR,
      min_price: minPrice,
      max_price: maxPrice,
      plan_mode: planMode,
      react_buy_box: false,
      market_place: marketPlace.marketPlace,
      product_list: selectedProducts.join(","),
      category_id: category,
      competitor_value: competitorValue,
      competitor_unit: competitorUnit,
      competitor_action: competitorAction.value,
      competitor_asin: chosenCompetitor,
      is_multiple_plans: createManyPlans,
      override_margin: overrideMargin,
      override_margin_value: overrideMarginValue,
      override_volume: overrideVolume,
      override_volume_value: overrideVolumeValue,
      override_supply: overrideSupply,
      override_supply_value: overrideSupplyValue,
      sales_net: salesNet,
    };

    if (planType?.programTemplate) {
      payload["program"] = {
        template_type: planType.programTemplate,
        ui_state: programForm.getValues(),
      };
    }

    stepHelpers.setStep("done");
    const res = await api.post("/pricing/pricingplan", {
      plan_options: payload,
    });

    queryClient.invalidateQueries({
      queryKey: ["planData"],
    });
    queryClient.invalidateQueries({
      queryKey: ["pricing_plan_report_dashboard"],
    });
    queryClient.invalidateQueries({
      queryKey: ["product_report"],
    });

    if (planStrategy.value === "rules_engine") {
      navigate({
        to: "/user/pricing/plan/$plan",
        params: { plan: String(res.data.plan_id) },
        search: { tab: "rules_engine" },
      });
    }
  };

  const enrollPlan = async () => {
    const errors = {};

    if (!disableMinMax) {
      if (minPrice === null || minPrice === "" || parseFloat(minPrice) <= 0) {
        errors.minPrice = t(($) => $.pricingv2["Please enter valid minimum price"]);
      }
      if (maxPrice === null || maxPrice === "" || parseFloat(maxPrice) <= 0) {
        errors.maxPrice = t(($) => $.pricingv2["Please enter valid maximum price"]);
      }
      if (minPrice && maxPrice && parseFloat(minPrice) >= parseFloat(maxPrice)) {
        errors.maxPrice = t(($) => $.pricingv2["Maximum price should be greater than minimum price"]);
      }
    }

    if (planStrategy.value === "bsr") {
      if (targetBSR === null || targetBSR === "" || parseFloat(targetBSR) <= 0) {
        errors.targetBSR = t(($) => $.pricingv2["Please enter valid target BSR"]);
      }
    }

    if (planStrategy.value === "competitor") {
      if (competitorAction.value !== "match") {
        if (competitorValue === null || competitorValue === "" || parseFloat(competitorValue) <= 0) {
          errors.competitorValue = t(($) => $.pricingv2["Please enter valid value"]);
        }
      }

      if (chosenCompetitor === null) {
        errors.chosenCompetitor = t(($) => $.pricingv2["Please select competitor"]);
      }
    }
    let isProgramValid = true;
    if (planStrategy?.programTemplate) {
      isProgramValid = await programForm.trigger();
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0 || !isProgramValid) {
      return;
    }

    stepHelpers.setStep("preview");
  };

  const { data: competitors, loading: loadingCompetitors } = useFetch(
    ["competitors"],
    "/api/competitors",
    {
      marketplace: marketPlace.marketPlace,
      product: selectedProducts[0],
    },
    { enabled: selectedProducts.length > 0 },
  );

  const asinList = useMemo(() => {
    if (competitors?.data?.results) {
      return competitors?.data?.results?.map((c) => c.product_ref);
    }
    return [];
  }, [competitors]);

  useEffect(() => {
    if (asinList && asinList.length > 0 && !loadingCompetitors) {
      const getPrices = async () => {
        const res = await api.post("/merchandise/competitive_price", {
          params: {
            asinList: asinList.join(","),
            marketplace: marketPlace.marketPlace,
          },
        });

        const prices = res.data.prices;
        const cl = [...competitors.data.results];
        for (let i = 0; i < prices.length; i++) {
          for (let j = 0; j < cl.length; j++) {
            if (cl[j].product_ref === prices[i].asin) {
              cl[j].price = prices[i].price;
              break;
            }
          }
        }

        setCompetitorList(cl);
      };

      getPrices();
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [asinList]);

  const getBSR = async (productId) => {
    const res = await api.get(`/pricing/bsr?product_id=${productId}`);
    setCurrentBSR(res.data?.ranks);
    setTargetBSR(res.data?.ranks);
    return res.data?.ranks;
  };

  useEffect(() => {
    if (selectedProducts[0]) {
      getBSR(selectedProducts[0]);
    }
  }, [selectedProducts]);

  const showPricingStrategy = useMemo(() => {
    if (loadingCompetitors) {
      return false;
    }

    if (planStrategy.value !== "dynamic") {
      return false;
    }

    if (overrideSupply) {
      return true;
    }

    return true;
  }, [loadingCompetitors, planStrategy, overrideSupply]);

  const { showEstimates, disableMinMax } = useMemo(() => {
    if (selectedProducts.length === 1) {
      return { showEstimates: true, disableMinMax: false };
    }

    if (createManyPlans) {
      return { showEstimates: false, disableMinMax: true };
    }

    return { showEstimates: false, disableMinMax: false };
  }, [createManyPlans, selectedProducts]);

  const problems = findProblemsWithPlans(previewQuery.data, productById, marketPlace.marketPlace, t);
  const noInitialPricePlans = previewQuery.data?.filter((pd) => !pd.current_price);
  const showOffsetRecommendation = selectedProducts.length > 1 && isMultiAsin;

  return (
    <Modal show={showEnroll} size="xl">
      <Modal.Header className="py-0 px-5">
        <div className="fs-3 my-4 text-start fw-bold">
          {createManyPlans ?
            t(($) => $.pricingv2["Create New Pricing Plan - Multiple Plans"])
          : t(($) => $.pricingv2["Create New Pricing Plan - Single Plan"])}
        </div>
      </Modal.Header>
      <Modal.Body>
        {!createManyPlans && showOffsetRecommendation && (currentStep === "group" || currentStep === "enroll") && (
          <Row className="px-3">
            <Col xs={12} className="pb-4">
              <ExplainerBox
                backgroundColor={"yellow/50"}
                className={css({ display: "flex", gap: "3", alignItems: "flex-start" })}
              >
                <IoWarningOutline className={css({ fontSize: "xlarge", flexShrink: 0 })} />
                <VStack gap="small" alignItems="flex-start">
                  <span className={css({ fontWeight: "semibold" })}>
                    {t(($) => $.pricingv2["Planning to use price offsets?"])}
                  </span>
                  <span>
                    {t(
                      ($) =>
                        $.pricingv2[
                          "Start with a single-plan setup using the lowest-priced product. Once the plan is created, add the remaining products that need offsets."
                        ],
                    )}
                  </span>
                </VStack>
              </ExplainerBox>
            </Col>
          </Row>
        )}
        {currentStep === "group" && (
          <Row className="fs-4 text-left py-3 px-3">
            <Col xs={12} className="fw-bold">
              {t(($) => $.pricingv2["How would you like to group your products?"])}
            </Col>
            <Col xs={12} className="fs-4 pt-2 pb-5">
              {t(
                ($) =>
                  $.pricingv2[
                    "If you've chosen similar ASINs from the products table and want to price them identically, you can consolidate them under one pricing plan. This is useful, for example, if you have the same product in different colors but want to maintain the same price for all variants."
                  ],
              )}
              <br />
              <br />
              {t(
                ($) =>
                  $.pricingv2[
                    "If your products are distinct or you prefer different prices for variations, opt for the multiple plan option."
                  ],
              )}
            </Col>
            <Col xs={12} className="text-start fs-4 pb-3 pt-3">
              <Form.Check
                type="radio"
                name="createManyPlans"
                label={
                  <span>
                    <span className="fw-bold">{t(($) => $.pricingv2["Single Plan"])}</span>:&nbsp;
                    {t(($) => $.pricingv2["A Single Pricing Plan which includes all selected ASINs"])}
                  </span>
                }
                checked={!createManyPlans}
                onChange={() => {
                  setCreateManyPlans(false);
                }}
              />
            </Col>
            <Col xs={12} className="text-start fs-4">
              <Form.Check
                type="radio"
                name="createManyPlans"
                label={
                  <span>
                    <span className="fw-bold">{t(($) => $.pricingv2["Multiple Plans"])}</span>:&nbsp;
                    {t(($) => $.pricingv2["A Pricing Plan for each chosen ASIN"])}
                  </span>
                }
                checked={createManyPlans}
                onChange={() => {
                  setCreateManyPlans(true);
                }}
              />
            </Col>
          </Row>
        )}
        {currentStep === "done" && (
          <Row className="fs-3 text-left py-3 px-3">
            <Col>
              <Row>
                <Col className="pt-2 pb-4 text-center fs-4 fw-bold">
                  {t(
                    ($) =>
                      $.pricingv2[
                        "Your pricing plan is currently being set up. This process may take a few moments to finish."
                      ],
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        {currentStep === "preview" && (
          <PreviewStep
            productById={productById}
            previewQuery={previewQuery}
            problems={problems}
            intentionValidator={intentionValidator}
            setIntentionValidator={setIntentionValidator}
            noInitialPricePlans={noInitialPricePlans}
          />
        )}
        {currentStep === "enroll" && (
          <>
            <Row className="fs-4 text-left py-3 px-3">
              <Col>
                <div>
                  <span className="fw-bold">{t(($) => $.pricingv2["Plan Setup"])}</span>
                </div>
              </Col>
            </Row>

            <Row className="fs-4 text-left py-3 px-5">
              <Col xs={7} className="ps-3">
                <Row>
                  {/*  Min Price */}
                  <Col xs={6} className="ps-3">
                    <CurrencyInput
                      label={t(($) => $.pricingv2["Minimum Price"])}
                      onChange={(value) => setMinPrice(value)}
                      value={disableMinMax ? null : minPrice}
                      isDisabled={disableMinMax}
                      minValue={0}
                      isRequired
                      errorMessage={validationErrors["minPrice"]}
                    />
                  </Col>
                  {/*  Max Price */}
                  <Col xs={6} className="ps-3">
                    <CurrencyInput
                      label={t(($) => $.pricingv2["Maximum Price"])}
                      onChange={(value) => setMaxPrice(value)}
                      value={disableMinMax ? null : maxPrice}
                      isDisabled={disableMinMax}
                      minValue={0}
                      isRequired
                      errorMessage={validationErrors["maxPrice"]}
                    />
                  </Col>

                  {/*  Sales Net */}
                  {hasPriceDebugModule && (
                    <Col xs={6} className="ps-3 pt-3">
                      <NumberInput
                        label={
                          <>
                            {" "}
                            <span>{t(($) => $.pricingv2["Sales Net"])}</span>
                            <HoverInfo>
                              {t(($) => $.pricingv2["Volume recovery allowance for post-deal periods"])}
                            </HoverInfo>{" "}
                          </>
                        }
                        onChange={(value) => setSalesNet(value)}
                        value={salesNet}
                        minValue={0}
                        formatOptions={{ maximumFractionDigits: 0 }}
                      />
                    </Col>
                  )}

                  <Col xs={12} className="pt-3">
                    {createManyPlans && (
                      <small>
                        {t(
                          ($) =>
                            $.pricingv2[
                              "The product settings will determine the range of minimum and maximum prices to be used."
                            ],
                        )}
                      </small>
                    )}
                    {!createManyPlans && (
                      <small>
                        {t(
                          ($) =>
                            $.pricingv2[
                              "Verify the minimum and maximum prices to be applied in your pricing strategy."
                            ],
                        )}
                      </small>
                    )}
                  </Col>
                </Row>
              </Col>
              <Col xs={5} className="ps-3">
                <span style={{ color: "#000" }}>{t(($) => $.pricingv2["Plan Options:"])}</span>
                <Row>
                  <Col>
                    <FormGroupTile
                      type="select"
                      multi={false}
                      stateName={"strategyType"}
                      handleChildFormElement={(key, value) => {
                        setPlanStrategy(value);
                      }}
                      defaultValue={planStrategy}
                      options={strategyOptions}
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={5} className="ps-3"></Col>
            </Row>

            {planStrategy.value === "competitor" && (
              <>
                <Row
                  className="fs-4 text-left pt-5 pb-3 px-3"
                  style={{
                    borderTop: "1px solid #ccc",
                  }}
                >
                  <Col>
                    <div className="fw-bold">{t(($) => $.pricingv2["Competitor Tracking"])}</div>
                  </Col>
                  <Col>
                    {validationErrors["chosenCompetitor"] && (
                      <div style={{ color: "red" }}>
                        <small>{validationErrors["chosenCompetitor"]}</small>
                      </div>
                    )}
                  </Col>
                </Row>
                <Row className="fs-4 text-center pb-1 px-5">
                  <Col>
                    {loadingCompetitors && (
                      <div className="text-center">
                        <span className="fw-bold pb-3">{t(($) => $.pricingv2["Competitors are loading..."])}</span>
                      </div>
                    )}
                    {competitorList.length === 0 && (
                      <div className="text-center">
                        <span className="fw-bold pb-3">
                          {t(($) => $.pricingv2["There are no competitors for this product"])}
                        </span>
                      </div>
                    )}
                    {competitorList.length > 0 && (
                      <Table>
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}>&nbsp;</th>
                            <th className="text-start">{t(($) => $.pricingv2["ASIN"])}</th>
                            <th className="text-start">{t(($) => $.pricingv2["Product Title"])}</th>
                            <th className="text-center">{t(($) => $.pricingv2["Price"])}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {competitors?.data?.results?.map((competitor, index) => {
                            if (!chosenCompetitor || chosenCompetitor === competitor.product_ref) {
                              return (
                                <tr key={index}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={chosenCompetitor === competitor.product_ref}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setChosenCompetitor(competitor.product_ref);
                                        } else {
                                          setChosenCompetitor(null);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className="text-start">
                                    <a
                                      href={`${PRODUCT_AMAZON_LINKS[marketPlace.marketPlace]}${competitor.product_ref}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {competitor.product_ref}
                                    </a>
                                  </td>
                                  <td className="text-start">{cut(competitor.title, 60)}</td>
                                  <td className="text-end">
                                    {formatCurrency(competitor.price, {
                                      marketPlace: marketPlace.marketPlace,
                                    })}
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          })}
                        </tbody>
                      </Table>
                    )}

                    {chosenCompetitor && (
                      <Row
                        className="pb-3 pt-3 mt-3"
                        style={{
                          borderTop: "1px solid #ccc",
                        }}
                      >
                        <Col xs={4} className="text-start">
                          <FormGroupTile
                            label={t(($) => $.pricingv2["I want my price to"])}
                            type="select"
                            multi={false}
                            stateName={"actionType"}
                            handleChildFormElement={(key, value) => {
                              setCompetitorAction(value);
                            }}
                            defaultValue={competitorAction}
                            options={actionTypeOptions}
                          />
                        </Col>
                        <Col xs={4} className="text-center">
                          <FormGroupTile
                            label={
                              competitorUnit === "percent" ?
                                competitorAction.value === "decrease" ?
                                  t(($) => $.pricingv2["by this percentage or less"])
                                : t(($) => $.pricingv2["by this percentage or more"])
                              : competitorAction.value === "decrease" ?
                                t(($) => $.pricingv2["by this amount or less"])
                              : t(($) => $.pricingv2["by this amount or more"])
                            }
                            formControlClass="text-center"
                            type="formcontrol"
                            formControlType={"number"}
                            prefixSide={"left"}
                            disabled={competitorAction.value === "match"}
                            defaultValue={competitorValue ? formatNumber(competitorValue, { decimalPlaces: 0 }) : 0}
                            handleChildFormElement={(key, value) => {
                              setCompetitorValue(value);
                            }}
                          />

                          {validationErrors["competitorValue"] && (
                            <span style={{ color: "red" }}>
                              <small>{validationErrors["competitorValue"]}</small>
                            </span>
                          )}
                        </Col>
                        <Col xs={4}>
                          <FormGroupLabel label={t(($) => $.pricingv2["Action Unit"])} titleCase={true} />
                          <Row className="pb-2">
                            <Col xs={3}></Col>
                            <Col xs={6}>
                              <div className="d-flex py-3 align-items-center justify-content-between">
                                <Checkbox
                                  label="%"
                                  value={competitorUnit === "percent"}
                                  onChange={() => {
                                    setCompetitorUnit("percent");
                                  }}
                                  isDisabled={competitorAction.value === "match"}
                                />

                                <Checkbox
                                  label="$"
                                  value={competitorUnit === "currency"}
                                  onChange={() => {
                                    setCompetitorUnit("currency");
                                  }}
                                  isDisabled={competitorAction.value === "match"}
                                />
                              </div>
                            </Col>
                            <Col xs={3}></Col>
                          </Row>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </>
            )}

            {showPricingStrategy && (
              <>
                <Row className="pt-5 mt-3 fs-4 px-3" style={{ borderTop: "1px solid #ccc" }}>
                  <Col xs={4}>
                    <span className="fw-bold">{t(($) => $.pricingv2["Pricing Strategy"])}</span>
                  </Col>
                  <Col xs={8} className="text-center">
                    {createManyPlans && (
                      <div
                        style={{
                          backgroundColor: trellisPalette[0],
                          color: "#fff",
                          borderRadius: "3px",
                        }}
                        className="p-2 fw-bold"
                      >
                        {t(($) => $.pricingv2["NOTE: Each Pricing Plan will follow the same configuration strategy."])}
                      </div>
                    )}
                  </Col>
                </Row>

                <Row className="fs-4 text-left pb-1 px-3">
                  <Col xs={3}>
                    <Row className="pt-3 mt-3 fs-4 px-3">
                      <Col xs={12} className="text-start">
                        <span
                          style={{
                            color: "#000",
                          }}
                        >
                          {t(($) => $.pricingv2["Target Volume (Monthly)"])}
                          <span
                            className="ps-2"
                            style={{
                              cursor: !overrideMargin ? "pointer" : "inherit",
                              color: trellisPalette[0],
                            }}
                            onClick={() => {
                              if (overrideMargin) {
                                return false;
                              }
                              setOverrideVolume(!overrideVolume);
                              setPlanMode(0);
                            }}
                          >
                            <TbPencilCog />
                          </span>
                        </span>
                        <Row className="pt-2 pb-4">
                          <Col xs={12}>
                            {overrideVolume && (
                              <>
                                <FormGroupTile
                                  formControlClass="text-center"
                                  type="formcontrol"
                                  formControlType={"numeric"}
                                  prefixSide={"left"}
                                  handleChildFormElement={(key, value) => {
                                    setOverrideVolumeValue(value);
                                  }}
                                  placeholder={t(($) => $.pricingv2["Target Volume"])}
                                  defaultValue={overrideVolumeValue}
                                  disabled={false}
                                  style={{
                                    border: validationErrors["targetVolume"] ? "1px solid red" : null,
                                  }}
                                />

                                {validationErrors["targetVolume"] && (
                                  <Row>
                                    <Col xs={12}>
                                      <span style={{ color: "red" }}>
                                        <small>{validationErrors["targetVolume"]}</small>
                                      </span>
                                    </Col>
                                  </Row>
                                )}
                              </>
                            )}
                            {!overrideVolume && (
                              <Row>
                                <Col xs={12} className="text-center">
                                  <div
                                    style={{
                                      backgroundColor: "#eee",
                                      border: "1px solid #e3e3e3",
                                      borderRadius: "45px",
                                      color: "#850066",
                                      padding: "8px 12px",
                                      height: "40px",
                                      boxshadow: "none",
                                    }}
                                  >
                                    -
                                  </div>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={12} className="text-start">
                        <span
                          style={{
                            color: "#000",
                          }}
                        >
                          {t(($) => $.pricingv2["Target Margin (%)"])}
                          <span
                            className="ps-2"
                            style={{
                              cursor: !overrideVolume ? "pointer" : "inherit",
                              color: trellisPalette[0],
                            }}
                            onClick={() => {
                              if (overrideVolume) {
                                return false;
                              }
                              setOverrideMargin(!overrideMargin);
                              setPlanMode(0);
                            }}
                          >
                            <TbPencilCog />
                          </span>
                        </span>
                        <Row className="pt-2 pb-4">
                          <Col xs={12}>
                            {overrideMargin && (
                              <>
                                <FormGroupTile
                                  formControlClass="text-center"
                                  type="formcontrol"
                                  formControlType={"numeric"}
                                  prefixSide={"left"}
                                  handleChildFormElement={(key, value) => {
                                    setOverrideMarginValue(parseFloat(value));
                                  }}
                                  placeholder={t(($) => $.pricingv2["Target Margin"])}
                                  defaultValue={overrideMarginValue}
                                  disabled={false}
                                  style={{
                                    border: validationErrors["targetMargin"] ? "1px solid red" : null,
                                  }}
                                />

                                {validationErrors["targetMargin"] && (
                                  <Row>
                                    <Col xs={12}>
                                      <span style={{ color: "red" }}>
                                        <small>{validationErrors["targetMargin"]}</small>
                                      </span>
                                    </Col>
                                  </Row>
                                )}
                              </>
                            )}

                            {!overrideMargin && (
                              <Row>
                                <Col xs={12} className="text-center">
                                  <div
                                    style={{
                                      backgroundColor: "#eee",
                                      border: "1px solid #e3e3e3",
                                      borderRadius: "45px",
                                      color: "#850066",
                                      padding: "8px 12px",
                                      height: "40px",
                                      boxshadow: "none",
                                    }}
                                  >
                                    -
                                  </div>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={12} className="text-center">
                        <span
                          style={{
                            color: "#000",
                          }}
                        >
                          {/* Supply Target Optimizer */}
                          {/* Stock Preservation Days Limit */}
                          {t(($) => $.pricingv2["Days on Hand Target"])}
                          <span
                            className="ps-2"
                            style={{
                              cursor: "pointer",
                              color: trellisPalette[0],
                            }}
                            onClick={() => {
                              setOverrideSupply(!overrideSupply);
                              setPlanMode(0);
                            }}
                          >
                            <TbPencilCog />
                          </span>
                        </span>
                        <Row className="pt-2 pb-4">
                          <Col xs={12}>
                            {overrideSupply && (
                              <>
                                <FormGroupTile
                                  formControlClass="text-center"
                                  type="formcontrol"
                                  formControlType={"numeric"}
                                  prefixSide={"left"}
                                  handleChildFormElement={(key, value) => {
                                    if (value) {
                                      setOverrideSupplyValue(parseFloat(value));
                                    } else {
                                      setOverrideSupplyValue(0);
                                    }
                                  }}
                                  placeholder={t(($) => $.pricingv2["Supply Target"])}
                                  defaultValue={overrideSupplyValue > 0 ? overrideSupplyValue.toFixed(0) : ""}
                                  disabled={false}
                                  style={{
                                    border: validationErrors["supply"] ? "1px solid red" : null,
                                  }}
                                />

                                {validationErrors["supply"] && (
                                  <Row>
                                    <Col xs={12}>
                                      <span style={{ color: "red" }}>
                                        <small>{validationErrors["supply"]}</small>
                                      </span>
                                    </Col>
                                  </Row>
                                )}
                              </>
                            )}

                            {!overrideSupply && (
                              <Row>
                                <Col xs={12} className="text-center">
                                  <div
                                    style={{
                                      backgroundColor: "#eee",
                                      border: "1px solid #e3e3e3",
                                      borderRadius: "45px",
                                      color: "#850066",
                                      padding: "8px 12px",
                                      height: "40px",
                                      boxshadow: "none",
                                    }}
                                  >
                                    {formatNumber(overrideSupplyValue, { decimalPlaces: 0 })}
                                  </div>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={9}>
                    {(overrideMargin || overrideVolume) && (
                      <Row className="fs-5 text-center pb-1 px-3">
                        <Col xs={12} className="text-start fs-5">
                          <div
                            style={{
                              padding: "2rem",
                              border: "1px solid rgb(238, 238, 238)",
                              borderRadius: "6px",
                              backgroundColor: "rgb(248, 248, 248)",
                              marginTop: "10px",
                              minHeight: "145px",
                            }}
                          >
                            <div>
                              {t(
                                ($) =>
                                  $.pricingv2[
                                    "Dynamic Pricing adjusts prices to align with your specified target volume or margin goals. It's important to understand that setting an overly ambitious target volume can impact your profit margins. However, a beneficial side effect might include improvements in your organic ranking and Best Seller Rank."
                                  ],
                              )}
                            </div>
                            <div className="pt-3">
                              {t(
                                ($) =>
                                  $.pricingv2[
                                    "Conversely, pushing for a significantly higher Target Margin could potentially harm your organic ranking and Best Seller Rank. To mitigate this risk while aiming to enhance your Target Margin, we suggest a cautious approach of increasing it by only 1% to 5%."
                                  ],
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}
                    {overrideSupply && (
                      <Row className="fs-5 text-center pb-1 px-3">
                        <Col xs={12} className="text-start fs-5">
                          <div
                            style={{
                              padding: "2rem",
                              border: "1px solid rgb(238, 238, 238)",
                              borderRadius: "6px",
                              backgroundColor: "rgb(248, 248, 248)",
                              marginTop: "10px",
                            }}
                          >
                            <div>
                              {t(
                                ($) =>
                                  $.pricingv2[
                                    "When the Days of Supply drops below your desired number of days, the dynamic pricing engine will automatically increase prices to manage inventory levels. Once the Days of Supply has recovered, prices will adjust back to the optimal or 'right' price to ensure competitiveness and profitability"
                                  ],
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}
                    {!overrideMargin && !overrideVolume && !overrideSupply && (
                      <>
                        <Row className="fs-5 text-center pb-1 pb-3 mt-3">
                          <Col xs={12} className="text-start fs-5">
                            <div>
                              {t(($) => $.pricingv2["Utilize the strategy slider to tailor your pricing model."])}
                            </div>
                            <HStack alignItems={"stretch"}>
                              {showEstimates && (
                                <MonthlyEstimates
                                  selectedProducts={selectedProducts}
                                  strategySlider={planMode}
                                  className={css({ flex: 1 })}
                                />
                              )}
                              <ExplainerBox flex={1}>{t(SLIDER_HELP[planMode + 2].description)}</ExplainerBox>
                            </HStack>
                          </Col>
                        </Row>

                        <Row className="fs-4 text-left pt-3 pb-5 px-5">
                          <Col className="col-md-5ths">
                            <PlanSlider
                              value={planMode}
                              onChange={setPlanMode}
                              isDisabled={overrideMargin || overrideVolume}
                            />
                          </Col>
                        </Row>
                      </>
                    )}
                  </Col>
                </Row>
              </>
            )}

            {planStrategy.value === "bsr" && (
              <>
                <Row
                  className="fs-4 text-left pt-5 pb-3 px-3"
                  style={{
                    borderTop: "1px solid #ccc",
                  }}
                >
                  <Col>
                    <div className="fw-bold">{t(($) => $.pricingv2["Best Seller Strategy"])}</div>
                  </Col>
                </Row>
                <Row className="fs-4 text-center py-3 px-5">
                  <Col xs={4}>
                    <div className="py-3">{t(($) => $.pricingv2["Category"])}</div>
                    <FormGroupTile
                      formControlClass="text-center"
                      type="formcontrol"
                      formControlType={"center"}
                      prefixSide={"left"}
                      disabled={true}
                      defaultValue={productById[selectedProducts[0]]?.amazon_category}
                    />
                  </Col>
                  <Col xs={4}>
                    <div className="py-3">{t(($) => $.pricingv2["Current BSR"])}</div>
                    <FormGroupTile
                      formControlClass="text-center"
                      type="formcontrol"
                      formControlType={"center"}
                      prefixSide={"left"}
                      disabled={true}
                      defaultValue={currentBSR ? formatNumber(currentBSR, { decimalPlaces: 0 }) : 0}
                    />
                  </Col>
                  <Col xs={4}>
                    <div className="py-3">{t(($) => $.pricingv2["Target BSR"])}</div>
                    <FormGroupTile
                      formControlClass="text-center"
                      type="formcontrol"
                      formControlType={"center"}
                      prefixSide={"left"}
                      handleChildFormElement={(key, value) => {
                        setTargetBSR(value);
                      }}
                      placeholder={t(($) => $.pricingv2["Target BSR"])}
                      defaultValue={targetBSR ? formatNumber(targetBSR, { decimalPlaces: 0 }) : 0}
                      style={{
                        border: validationErrors["targetBSR"] ? "1px solid red" : null,
                      }}
                      disabled={targetBSR === null}
                    />

                    {validationErrors["targetBSR"] && (
                      <Row>
                        <Col xs={12}>
                          <span style={{ color: "red" }}>
                            <small>{validationErrors["targetBSR"]}</small>
                          </span>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </>
            )}
            {!!planStrategy?.programTemplate && planStrategy?.programTemplate !== "CUSTOM" && (
              <Row
                className="fs-4 text-left pt-4 pb-3 px-3"
                style={{
                  borderTop: "1px solid #ccc",
                }}
              >
                <InlineProgramForm
                  size="sm"
                  form={programForm}
                  showHeaderFields={false}
                  programTemplate={planStrategy.programTemplate}
                />
              </Row>
            )}

            {planStrategy?.programTemplate === "CUSTOM" && (
              <Row
                className="fs-4 text-left pt-4 pb-3 px-3"
                style={{
                  borderTop: "1px solid #ccc",
                }}
              >
                <Col className="py-5 text-center">
                  <div className="fs-4 fw-bold">
                    {t(
                      ($) =>
                        $.pricingv2[
                          "Custom Rules will need to be created once the Pricing Plan has completed enrollment."
                        ],
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack justifyContent="space-evenly" width="100%">
          <HStack>
            {stepHelpers.canGoToPrevStep && currentStep !== "done" && (
              <Button variant="secondary" onPress={() => stepHelpers.goToPrevStep()}>
                {t(($) => $.pricingv2["<< Back"])}
              </Button>
            )}
            <Button variant="secondary" onPress={() => setShowEnroll(false)}>
              {t(($) => $.pricingv2["Close"])}
            </Button>
          </HStack>
          {currentStep === "group" && (
            <Button onPress={() => stepHelpers.goToNextStep()}>{t(($) => $.pricingv2["Next >>"])}</Button>
          )}
          {currentStep === "enroll" && (
            <Button
              isPending={planStrategy.value === "competitor" && competitorList.length === 0}
              onPress={() => enrollPlan()}
            >
              {t(($) => $.pricingv2["Preview Enrollment"])}
            </Button>
          )}
          {currentStep === "preview" && (
            <Button
              onPress={() => confirmPlan()}
              isDisabled={
                !previewQuery.isSuccess ||
                (problems.length > 0 && intentionValidator.toLowerCase() !== "create plans") ||
                !!noInitialPricePlans.length
              }
            >
              {t(($) => $.pricingv2["Confirm Enrollment"])}
            </Button>
          )}
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

function PreviewStep({
  productById,
  previewQuery,
  problems,
  intentionValidator,
  setIntentionValidator,
  noInitialPricePlans,
}) {
  const { t } = useTranslation(["pricingv2"]);
  return (
    <>
      <h4 className={css({ margin: 0 })}>{t(($) => $.pricingv2["These plans will be created"])}</h4>
      <p>
        {t(
          ($) =>
            $.pricingv2[
              "Once the plans are created, the price of all products in a plan will change to the plan's initial price"
            ],
        )}
      </p>

      {!!problems.length && !noInitialPricePlans?.length && (
        <VStack
          alignItems="start"
          border={"1px solid {black}"}
          paddingX={"large"}
          paddingY="medium"
          backgroundColor={"yellow/50"}
          fontSize="medium"
        >
          <h5 className={css({ margin: 0 })}>
            <IoWarningOutline className={css({ fontSize: "xxlarge" })} />{" "}
            {t(($) => $.pricingv2["There are some potential issues with the plans"])}
          </h5>
          <ul className={css({ margin: 0, padding: 0, listStylePosition: "inside" })}>
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
          {t(
            ($) =>
              $.pricingv2[
                'To proceed without fixing these, please confirm your intention by typing "create plans" in the box below'
              ],
          )}
          <TextField placeholder="create plans" value={intentionValidator} onChange={setIntentionValidator} />
        </VStack>
      )}
      {!!noInitialPricePlans?.length && (
        <VStack
          alignItems="start"
          border={"1px solid {black}"}
          paddingX={"large"}
          paddingY="medium"
          backgroundColor={"red/50"}
          fontSize="small"
        >
          <h5 className={css({ margin: 0 })}>
            <IoWarningOutline className={css({ fontSize: "xxlarge" })} />{" "}
            {t(
              ($) =>
                $.pricingv2[
                  "Your selection of products results in pricing plans with unknown price. Please close the modal and adjust your selection"
                ],
            )}
          </h5>
          <ul className={css({ margin: 0, padding: 0, listStylePosition: "inside" })}>
            {noInitialPricePlans.map((pd, i) => (
              <li key={i}>
                {t(($) => $.pricingv2["Plan ASIN: {asin}"], { asin: productById[pd.product_list[0]].asin })}
              </li>
            ))}
          </ul>
        </VStack>
      )}

      <Table>
        <thead>
          <tr>
            <th></th>
            <th>{t(($) => $.pricingv2["Plan title"])}</th>
            <th>{t(($) => $.pricingv2["SKU(s)"])}</th>
            <th>{t(($) => $.pricingv2["Current Price"])}</th>
            <th>{t(($) => $.pricingv2["Initial Price"])}</th>
            <th>{t(($) => $.pricingv2["Min Price"])}</th>
            <th>{t(($) => $.pricingv2["Max Price"])}</th>
          </tr>
        </thead>
        <tbody>
          {previewQuery.isLoading && (
            <tr>
              <td colSpan={6}>{t(($) => $.pricingv2["Loading..."])}</td>
            </tr>
          )}
          {previewQuery.isError && (
            <tr>
              <td colSpan={6}>
                <DevErrorDisplay
                  userMessage={t(($) => $.pricingv2["Error loading preview"])}
                  error={previewQuery.error}
                />
              </td>
            </tr>
          )}
          {previewQuery.data?.map((pd, i) => (
            <PreviewRow pd={pd} productById={productById} key={pd.product_list.join(",")} initialExpanded={i === 0} />
          ))}
        </tbody>
      </Table>
    </>
  );
}

function PreviewRow({ pd, productById, initialExpanded = false }) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const products = pd.product_list.map((id) => productById[id]);
  return (
    <>
      <tr>
        <td>
          <button
            onClick={() => setIsExpanded((value) => !value)}
            className={css({ border: "none", background: "transparent" })}
          >
            {isExpanded ?
              <FaCaretDown />
            : <FaCaretRight />}
          </button>
        </td>
        <td>
          <Flex gap={4} alignContent={"center"} alignItems={"center"}>
            <ProductImageURL url={products[0].product_image_url} width={35} height={35} />
            {pd.plan_name}
          </Flex>
        </td>
        <td>{pd.product_list.length}</td>
        <td></td>
        <td>
          {formatCurrency(pd.current_price, {
            marketPlace: marketPlace,
          })}
        </td>
        <td>
          {formatCurrency(pd.min_price, {
            marketPlace: marketPlace,
          })}
        </td>
        <td>
          {formatCurrency(pd.max_price, {
            marketPlace: marketPlace,
          })}
        </td>
      </tr>
      {isExpanded &&
        products.map((product) => (
          <tr key={product.id}>
            <td></td>
            <td
              colSpan={2}
              className={css({
                paddingLeft: "medium",
              })}
            >
              <Flex gap={4} alignContent={"center"} alignItems={"center"}>
                <ProductImageURL url={product.product_image_url} width={30} height={30} />
                <a
                  href={`${PRODUCT_AMAZON_LINKS[marketPlace]}${product.asin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {product.sku}
                </a>
              </Flex>
            </td>

            <td colSpan={4}>
              {formatCurrency(product.product_price, {
                marketPlace: marketPlace,
              })}
            </td>
          </tr>
        ))}
    </>
  );
}

function findProblemsWithPlans(planDefinitions = [], productsById, marketPlace, t) {
  const problems = [];
  for (const pd of planDefinitions) {
    const products = pd.product_list.map((id) => productsById[id]);
    const exampleProduct = products[0];
    if (pd.current_price < pd.min_price && Math.abs(percentChange(pd.current_price, pd.min_price)) > 15) {
      problems.push(
        t(
          ($) =>
            $.pricingv2[
              "Plan for ASIN {asin} has current price ({currentPrice}) significantly below minimum price ({minPrice})"
            ],
          {
            asin: exampleProduct.asin,
            currentPrice: formatCurrency(pd.current_price, { marketPlace: marketPlace }),
            minPrice: formatCurrency(pd.min_price, { marketPlace: marketPlace }),
          },
        ),
      );
    }
    if (pd.current_price > pd.max_price && Math.abs(percentChange(pd.current_price, pd.max_price)) > 15) {
      problems.push(
        t(
          ($) =>
            $.pricingv2[
              "Plan for ASIN {asin} has current price ({currentPrice}) significantly above maximum price ({maxPrice})"
            ],
          {
            asin: exampleProduct.asin,
            currentPrice: formatCurrency(pd.current_price, { marketPlace: marketPlace }),
            maxPrice: formatCurrency(pd.max_price, { marketPlace: marketPlace }),
          },
        ),
      );
    }
    if (products.length > 1) {
      const prices = products.map((p) => p.product_price).sort((a, b) => a - b);
      const diff = Math.abs(percentChange(prices.at(0), prices.at(-1)));
      if (diff > 15) {
        problems.push(
          t(
            ($) =>
              $.pricingv2[
                "Plan for ASIN {asin} has a large price range between products, min is {minPrice} and max is {maxPrice}"
              ],
            {
              asin: exampleProduct.asin,
              minPrice: formatCurrency(prices.at(0), { marketPlace: marketPlace }),
              maxPrice: formatCurrency(prices.at(-1), { marketPlace: marketPlace }),
            },
          ),
        );
      }
    }
  }
  return problems;
}
